import { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Prompt, useHistory, useLocation } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import { Location } from 'history';

import { validateSnippet } from 'api/SnippetsAPI';
import { selectUserProjectId } from 'core/userContext';
import { addNegativeToast, removeAllToasts } from 'modules/toastContainer';
import { FormField } from 'ui/formField';
import { Link } from 'ui/links';
import { HeaderTitle, SecondaryPageHeader } from 'ui/pageHeader';
import { InfoBanner, ErrorBanner } from 'ui/banner';
import { spacing } from 'ui/theme';
import { ResponseWrapper } from 'ui/toast';
import noOp from 'utils/noop';

import { EmptyStateNotFound } from '../EmptyState';
import { UnsavedChangesModal } from '../UnsavedChangesModal';
import { SnippetContentEditor } from '../SnippetContentEditor';
import { SnippetEditorActionButtons } from '../SnippetEditorActionButtons';
import { SnippetModals } from '../SnippetModals';
import {
  ErrorBannerContainer,
  BannerContainer,
  EditorContainer,
  FooterContainer,
  LeftColumn,
  MinHeightSkeleton,
  RightColumn,
  TwoColumnWrapper,
} from '../Styled';

import { ModalIntent, snippetUrlList } from '../../constants';
import { useCreateSnippet } from '../../hooks/useCreateSnippet';
import { useUpdateSnippet } from '../../hooks/useUpdateSnippet';
import { useSnippetModals } from '../../hooks/useSnippetModals';
import { useSnippetById } from '../../hooks/useSnippetById';
import { useTranslations } from '../../hooks/useTranslations';

import {
  GraphQLSnippet,
  SnippetFieldsInput, // this is a type used for TS enforcement of field values
  SnippetFieldsInputIds, // this is a constant used for form input tracking
  SnippetFieldTestingIds,
  ValidationData,
  SnippetValidation,
  ValidationFailure,
  RequestType,
} from '../../types';

import { getFailedValidationMessage, getFailedValidationBannerMessage } from '../../utils';
import { SnippetDescription } from './SnippetDescription';
import { SnippetName } from './SnippetName';
import { SnippetPreview } from './SnippetPreview';
import { SnippetPositionalParameters } from './SnippetPositionalParameters';
import { SnippetEditorFormProvider } from './SnippetEditorFormProvider';
import { useFeatureFlag } from 'utils/hooks/useFeatureFlag';
import { isParameterOnlyChange } from 'utils/urlParams';

const SnippetEditorContainer = styled.div``;

export const SnippetEditorWithoutWrapper = ({ snippetId }: { snippetId: number }) => {
  const {
    clearErrors,
    reset,
    setError,
    setValue,
    watch,
    formState: { errors, isDirty: isSnippetDirty, defaultValues: lastSavedSnippetValues },
  } = useFormContext();
  const isCreativeLibraryAssetsFolderPanelFFEnabled = useFeatureFlag(
    'CreativeLibraryAssetsFolderPanel',
    false,
  );
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslations();
  const modals = useSnippetModals();
  const projectId = useSelector(selectUserProjectId);
  const saveSuccessCallback = useRef<() => void>(noOp);
  const urlInitial = useRef<string>(`${location.pathname}`);
  const [urlUpdated, setUrlUpdated] = useState<string>(urlInitial.current);
  const { isLoading, hasError, results: snippetWithAllFields } = useSnippetById({ id: snippetId });
  const isEditMode = !!snippetId;
  const editSnippet = watch();
  const [snippetInitContent, setSnippetInitContent] = useState<string>(editSnippet?.content || '');
  const [snippetHasLoaded, setSnippetHasLoaded] = useState<boolean>(false);
  const [hasValidationError, setHasValidationError] = useState(false);
  const [validationErrorMsg, setValidationErrorMsg] = useState<{ msg: string; code?: string }>({
    msg: '',
  });
  const [modalVisible, setModalVisible] = useState(false);
  const [lastLocation, setLastLocation] = useState<Location | null>(null);
  const [confirmedNavigation, setConfirmedNavigation] = useState(false);

  const snippetDeleted = useRef(false);

  /** Tracks errors in snippet fields upon validation */
  const handleErrors = (validationErrors: ValidationData[]) =>
    validationErrors.forEach((validationError) =>
      setError(validationError.fieldId, {
        type: 'custom',
        message: validationError.errorMsg as string,
      }),
    );

  const [createSnippet] = useCreateSnippet({
    onComplete: noOp,
    onError: (createSnippetErrors) => {
      // completely reset the errors based on the error return
      handleErrors(createSnippetErrors);
    },
    onSuccess: (updatedSnippet: GraphQLSnippet) => {
      // use the react lifecycle so that we can dismiss field changes before attempting to navigate
      const urlLoc = `/templates/snippet/${updatedSnippet.id}/edit`;
      // this preemptively resets the form's isDirty tracking to avoid unwanted unsaved changes message
      reset({}, { keepValues: true });
      if (saveSuccessCallback.current === noOp) {
        setUrlUpdated(urlLoc);
      } else {
        saveSuccessCallback.current();
      }
    },
  });

  const [updateSnippet] = useUpdateSnippet({
    onComplete: noOp,
    onError: (updateSnippetErrors) => {
      handleErrors(updateSnippetErrors);
    },
    onSuccess: () => {
      saveSuccessCallback.current();
    },
  });

  const handleValidation = (requestType: RequestType) => {
    const fields: SnippetFieldsInput = { ...(editSnippet as SnippetFieldsInput) };
    validateSnippet(
      projectId,
      editSnippet.content,
      editSnippet.name,
      editSnippet.positionalParameters,
    )
      .then(() => {
        setHasValidationError(false);
        setValidationErrorMsg({ msg: '' });

        if (requestType === RequestType.Create) {
          createSnippet({
            variables: { name: editSnippet.name, fields },
          });
        } else {
          updateSnippet({
            variables: { id: snippetId, fields },
          });
        }
      })
      .catch((error) => {
        const resp: ValidationFailure = error.response.data;
        const bannerMsg = getFailedValidationBannerMessage(resp);

        setHasValidationError(true);
        setValidationErrorMsg(bannerMsg);

        if (resp.type === SnippetValidation.HasCycle) {
          dispatch(
            addNegativeToast(
              <ResponseWrapper>
                {getFailedValidationMessage(resp)}
                <br />
                <Link
                  hasOpenNewTabIcon
                  href={'https://support.iterable.com/hc/articles/4415908141460'}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  Learn More.
                </Link>
              </ResponseWrapper>,
            ),
          );
        } else {
          dispatch(
            addNegativeToast(<ResponseWrapper>{getFailedValidationMessage(resp)}</ResponseWrapper>),
          );
        }
      });
  };

  const handleCopySnippet = () => {
    modals.save.toggle(false);
    modals.copy.toggle(true);
  };

  const handleUpdateSnippet = () => {
    modals.save.toggle(false);
    handleValidation(RequestType.Update);
  };

  const handleSaveSnippet = (successCallback = noOp) => {
    saveSuccessCallback.current = successCallback;
    if (isEditMode) {
      modals.save.toggle(true);
    } else {
      handleValidation(RequestType.Create);
    }
  };

  const openModal = () => {
    setModalVisible(true);
  };

  const closeModal = () => {
    setModalVisible(false);
  };

  /** Handles blocking React Routes */
  const handleBlockedNavigation = (nextLocation: Location): boolean => {
    if (!confirmedNavigation) {
      // Allow parameter-only changes without prompting (e.g., folderId changes)
      if (
        isCreativeLibraryAssetsFolderPanelFFEnabled &&
        isParameterOnlyChange(location, nextLocation, ['folderId'])
      ) {
        return true;
      }

      openModal();
      setLastLocation(nextLocation);
      return false;
    }
    return true;
  };

  const handleSaveAndExit = () => {
    closeModal();
    setModalVisible(false);
    handleSaveSnippet(() => setConfirmedNavigation(true));
  };

  const handleSnippetCopySuccess = (updatedSnippet: GraphQLSnippet) => {
    // use the react lifecycle so that we can dismiss field changes before attempting to navigate
    const urlLoc = `/templates/snippet/${updatedSnippet.id}/edit`;
    // this preemptively resets the form's isDirty tracking to avoid unwanted unsaved changes message
    reset({}, { keepValues: true });
    setHasValidationError(false);
    if (saveSuccessCallback.current === noOp) {
      setUrlUpdated(urlLoc);
    } else {
      saveSuccessCallback.current();
    }
  };

  const handleSnippetDeleteSuccess = () => {
    // use the react lifecycle so that we can dismiss field changes before attempting to navigate
    snippetDeleted.current = true;
    setUrlUpdated(snippetUrlList);
  };

  const handleLeaveWithoutSaving = () => {
    closeModal();
    setConfirmedNavigation(true);
  };

  const handleOnChangeContent = (newContent: string, isInitValue?: boolean) => {
    setValue(SnippetFieldsInputIds.content, newContent, { shouldDirty: true });
    clearErrors(SnippetFieldsInputIds.content);
    if (isInitValue) {
      setSnippetInitContent(newContent);
    }
  };

  const handleToastsRemoval = () => {
    if (!snippetDeleted.current) {
      dispatch(removeAllToasts());
    }
  };

  useEffect(() => {
    if (!isLoading && !snippetHasLoaded) {
      setValue(SnippetFieldsInputIds.content, editSnippet?.content || '');
      setSnippetInitContent(editSnippet?.content || '');
      setSnippetHasLoaded(true);
    }
  }, [isLoading, editSnippet]);

  useEffect(() => {
    if (urlUpdated !== urlInitial.current) {
      if (isEditMode) {
        // After successfully copying new snippet, update the url
        history.push(urlUpdated);
      } else {
        // After successfully saving a new snippet, update the url
        history.replace(urlUpdated);
      }
    }
  }, [isEditMode, urlUpdated]);

  useEffect(() => {
    if (confirmedNavigation && lastLocation) {
      // Navigate to the previous blocked location with your navigate function
      history.push(lastLocation as any as string);
    }
  }, [confirmedNavigation, lastLocation]);

  /** Displays browser specific dialog to alert the user of unsaved changes for routes not handled in React. */
  useEffect(() => {
    if (!isSnippetDirty) {
      return;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();

      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isSnippetDirty]);

  /** Remove toasts when leaving editor page */
  useEffect(() => {
    return () => {
      handleToastsRemoval();
    };
  }, []);

  if (isEditMode && (hasError || (!isLoading && editSnippet.id === 0))) {
    return <EmptyStateNotFound />;
  }

  return (
    <SnippetEditorContainer>
      <Prompt when={isSnippetDirty} message={handleBlockedNavigation} />

      {modalVisible && (
        <UnsavedChangesModal
          onClose={closeModal}
          onLeave={handleLeaveWithoutSaving}
          onSave={handleSaveAndExit}
          isModalOpen={modalVisible}
        />
      )}

      <SecondaryPageHeader
        breadcrumb={{ href: '/templates/snippets', content: t('breadcrumb.allSnippets') }}
        documentTitle={`${isEditMode ? editSnippet.name : t('header.newSnippet')} | Snippets`}
        pageActions={
          <SnippetEditorActionButtons
            handleSaveSnippet={handleSaveSnippet}
            hasErrors={Object.keys(errors).length > 0}
            showOverflowMenu={isEditMode}
            showUnsavedChangesMessage={isSnippetDirty}
            snippet={{ ...snippetWithAllFields, ...editSnippet }}
          />
        }
        pageSubSection={<>{isEditMode && `ID: ${snippetId}`}</>}
        widthStyle={spacing.PageWidthStyle.normal}
      >
        <HeaderTitle>{isEditMode ? editSnippet.name : t('header.newSnippet')}</HeaderTitle>
      </SecondaryPageHeader>
      {isEditMode && (
        <BannerContainer>
          <InfoBanner>{t('banner.displayChanges')}</InfoBanner>
        </BannerContainer>
      )}
      <TwoColumnWrapper>
        <LeftColumn>
          {!isEditMode && <SnippetName />}
          <SnippetDescription />
          <SnippetPositionalParameters />
        </LeftColumn>
        <RightColumn>
          <SnippetPreview isCopyDisabled={isSnippetDirty} isEditMode={isEditMode} />
        </RightColumn>
      </TwoColumnWrapper>

      {hasValidationError && (
        <ErrorBannerContainer>
          <ErrorBanner>
            {validationErrorMsg.msg}
            {!!validationErrorMsg.code && (
              <>
                {' '}
                <code>{validationErrorMsg.code}</code>
              </>
            )}
          </ErrorBanner>
        </ErrorBannerContainer>
      )}

      {SnippetFieldsInputIds.content in errors && (
        <ErrorBannerContainer>
          <ErrorBanner>
            <>{errors[SnippetFieldsInputIds.content]?.message}</>
          </ErrorBanner>
        </ErrorBannerContainer>
      )}

      <EditorContainer>
        <FormField inputId={SnippetFieldTestingIds.Content} labelText={t('label.snippetEditor')}>
          {!snippetHasLoaded ? (
            <MinHeightSkeleton />
          ) : (
            <SnippetContentEditor
              onChange={handleOnChangeContent}
              value={editSnippet.content}
              initialValue={snippetInitContent} // the value the editor is initialized with
              lastSavedValue={lastSavedSnippetValues?.content || ''} // gql hook maintains last saved value
            />
          )}
        </FormField>
      </EditorContainer>

      {/** Footer */}
      <FooterContainer>
        <SnippetEditorActionButtons
          handleSaveSnippet={handleSaveSnippet}
          hasErrors={Object.keys(errors).length > 0}
          isFooter
          showOverflowMenu={isEditMode}
          showUnsavedChangesMessage={isSnippetDirty}
          snippet={{ ...snippetWithAllFields, ...editSnippet }}
        />
      </FooterContainer>
      <SnippetModals
        handleCopy={handleCopySnippet}
        handlePublish={handleUpdateSnippet}
        intent={ModalIntent.edit}
        onCopySuccess={handleSnippetCopySuccess}
        onDeleteSuccess={handleSnippetDeleteSuccess}
        snippet={{ ...snippetWithAllFields, ...editSnippet }}
      />
    </SnippetEditorContainer>
  );
};

export const SnippetEditor = ({ match }: { match: any }) => {
  const snippetId = match.params.id ? parseInt(match.params.id, 10) : 0;
  return (
    <SnippetEditorFormProvider snippetId={snippetId}>
      <SnippetEditorWithoutWrapper snippetId={snippetId} />
    </SnippetEditorFormProvider>
  );
};
